import React, { useEffect } from 'react';
import { Spinner } from '@myhealth-raccoon/components';
import { useBeforeUnload } from 'react-use';
import { useNavigate } from 'react-router-dom';
import { captureException } from '@sentry/react';
import { isRaccoonError, useAsyncData } from '@myhealth-raccoon/services';

import { patientService } from 'src/services/patient';
import { useAppointmentTypeSelector, useClinicSelector, useDoctorSelector } from 'src/hooks/useAsyncSelector';
import { isFaceToFace } from 'src/utils/appointmentTypes';
import { useDispatch, useSelector } from 'src/store';
import { clinicName } from 'src/utils/name';
import { isRequestError } from 'src/utils/requestError';
import { stripHtml } from 'src/utils/html';
import { useBookingFlowStep } from 'src/hooks/useBookingFlowStep';
import { bookingService } from 'src/services/booking';

type Props = { to: string }

export function ConfirmBookingPassThrough({ to }: Props) {
    useBookingFlowStep(4);
    const navigate = useNavigate();
    // show dialog before closing/reloading
    useBeforeUnload();
    const dispatch = useDispatch();

    const site_id = useSelector(state => state.appointment.clinicId)
    const { appointment_id } = useSelector(state => state.appointment.reservedBooking!);
    const codeRequestType = useSelector(state => state.bookingFlow.codeRequestType);

    const { data: basicDetails } = useAsyncData({
        method: patientService.GetBasicDetails,
        service: patientService
    }, site_id)

    const { data: doctor } = useDoctorSelector();
    const { data: appointmentType } = useAppointmentTypeSelector();
    const { data: clinic } = useClinicSelector();

    useEffect(() => {
        if (!doctor || !appointmentType || !clinic || !basicDetails) {
            return;
        }

        (async () => {
            try {
                await bookingService.ConfirmBooking(site_id, appointment_id, {
                    sendSMS: codeRequestType !== "Email",
                    doctorProfileUrl: doctor.automedProfileUrl,
                    appointmentDuration: appointmentType.standardDurationInMins,
                    appointmentFaceToFace: isFaceToFace(appointmentType) ? "Face to Face" : "Telehealth",
                    clinicAddress: clinic.addressHtml,
                    clinicAddressUrl: `https://maps.google.com/?q=${stripHtml(clinic.addressHtml)}`,
                    clinicName: clinicName(clinic)!,
                    clinicTel: clinic.phoneNumber,
                    patientEmail: basicDetails.email,
                    patientFullName: `${basicDetails.firstname} ${basicDetails.surname}`,
                });
                navigate(to, { replace: true });
            } catch (e) {
                if (e instanceof Error) {
                    // IF return reason = 4, then Booking was created, but something failed in sending confirmation. 
                    // IF return reason = 3 - the slot time has expired.
                    if (isRequestError(e) && isRaccoonError(e.data)) {
                        if (e.data.reason === 4) {
                            navigate(to, { replace: true, state: { confirmNotifyFailed: true } });
                            return;
                        }
                        else if (e.data.reason === 3) {
                            navigate('/app/err-reserved-slot-expired', { replace: true });
                            return;
                        }
                    }
                    captureException(e)
                    navigate('/app/err-unexpected-error');
                }
            }
        })()
    }, [appointment_id, codeRequestType, navigate, site_id, doctor, appointmentType, clinic, basicDetails, to, dispatch])

    return <div className='space-y-2.5 my-auto'>
        <h1 className='text-2xl text-gray-900 font-semibold mb-2.5 text-center'>We're confirming your booking...</h1>
        <p className='text-base font-gray-600 text-center'>Please don't close your browser or use the back button.</p>
        <Spinner className='block mx-auto w-fit' />
    </div>
}